import MemberSearchAutocomplete from '../common/MemberSearchAutocomplete';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useMemberContext } from '../../hooks/useMemberContext';
import { amenityAPI, memberProfileAPI } from '../../services/api';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface BookingGuest {
  type: 'member' | 'external' | 'guest';
  memberId?: string | null;
  name?: string;
  email?: string;
  guest?: {
    name: string | null;
    email: string | null;
  };
}

interface MemberCharge {
  memberId: string;
  memberName: string;
  memberEmail: string;
  tierName: string;
  amount: number;
  tax: number;
  totalAmount: number;
  status: string;
  metadata?: any;
  isHost: boolean;
}

interface FeeBreakdown {
  totalBookingFee: number;
  totalPlayers: number;
  payingPlayers: number;
  feePerPlayer: number;
  guestFeePerPerson: number;
  taxRate: number;
  host: {
    bookingFee: number;
    guestFees: number;
    discount: number;
    discountPercentage: number;
    baseAmount: number;
    tax: number;
    totalAmount: number;
    tierAccess: string;
    shouldPay: boolean;
    isJunior: boolean;
  };
  memberGuests: Array<{
    memberId: string;
    name: string;
    email: string;
    tierName: string;
    bookingFee: number;
    discount: number;
    discountPercentage: number;
    baseAmount: number;
    tax: number;
    totalAmount: number;
    tierAccess: string;
    shouldPay: boolean;
    isJunior: boolean;
    reason?: string;
  }>;
  externalGuests: Array<{
    name: string;
    email: string;
    feePerGuest: number;
    chargedTo: string;
  }>;
  grandTotal: number;
}

interface AmenityBooking {
  id: string;
  amenityId: string;
  amenityName?: string;
  memberId: string;
  memberName: string; // Host's full name
  memberEmail: string; // Host's email
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  bookingFee: number;
  guests: BookingGuest[];
  notes?: string;
  amenity?: {
    id: string;
    name: string;
    cancellationWindowHours: number;
  };
  transactions?: MemberCharge[];
}

interface MemberProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  membershipNumber: string;
}

const MemberAmenityBooking: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user, memberProfile: currentMemberProfile } = useMemberContext();
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [booking, setBooking] = useState<AmenityBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Cancellation dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationWarning, setCancellationWarning] = useState<string | null>(null);
  const [withinCancellationWindow, setWithinCancellationWindow] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedGuests, setEditedGuests] = useState<BookingGuest[]>([]);
  const [saving, setSaving] = useState(false);
  
  
  // Fee breakdown state
  const [feeBreakdown, setFeeBreakdown] = useState<FeeBreakdown | null>(null);
  const [loadingFees, setLoadingFees] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  useEffect(() => {
    if (editMode) {
      calculateFees(editedGuests);
    }
  }, [editMode]);

  useEffect(() => {
    if (editMode && booking) {
      calculateFees(editedGuests);
    }
  }, [editedGuests]);

  // Scroll to top when error is set
  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Also try scrolling the window to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const fetchBookingDetails = async () => {
    if (!bookingId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First, get the booking to determine the amenityId
      // We'll need to search through dashboard bookings to get the amenity ID
      // Or we can extract it from the rawData
      
      // For now, we'll make a direct API call to get booking details
      // The amenityId should be in the booking response
      const response = await amenityAPI.getUserBookings();
      const userBookings = response.data || [];
      const targetBooking = userBookings.find((b: any) => b.id === bookingId);
      
      if (!targetBooking) {
        setError('Booking not found');
        setLoading(false);
        return;
      }
      
      // Now fetch full details with the amenity ID
      const detailsResponse = await amenityAPI.getAmenityBookingById(
        targetBooking.amenityId,
        bookingId
      );
      
      const bookingData = detailsResponse.data;
      
      // Fetch amenity details for cancellation window
      const amenityResponse = await amenityAPI.getAmenityById(targetBooking.amenityId);
      bookingData.amenity = amenityResponse.data;
      bookingData.amenityName = amenityResponse.data.name;
      
      // Enrich member guests with their full profile details
      if (bookingData.guests && bookingData.guests.length > 0) {
        const enrichedGuests = await Promise.all(
          bookingData.guests.map(async (guest: BookingGuest) => {
            if (guest.type === 'member' && guest.memberId) {
              try {
                const memberResponse = await memberProfileAPI.getMemberProfileById(guest.memberId);
                const memberData = memberResponse.data;
                return {
                  ...guest,
                  name: `${memberData.firstName} ${memberData.lastName}`,
                  email: memberData.email
                };
              } catch (err) {
                console.error('Error fetching member profile:', err);
                return guest; // Return original guest if fetch fails
              }
            } else if (guest.type === 'guest' && guest.guest) {
              // External guest - flatten the nested structure and normalize type
              return {
                ...guest,
                type: 'external', // Normalize to 'external' for consistency with new guests
                name: guest.guest.name || '',
                email: guest.guest.email || ''
              };
            }
            return guest;
          })
        );
        bookingData.guests = enrichedGuests;
      }
      
      setBooking(bookingData);
      setEditedGuests([...(bookingData.guests || [])]);
      
      // Check if booking is in the past - use date-only comparison to avoid timezone issues
      // bookingDate is YYYY-MM-DD format, startTime is HH:MM:SS format
      const [year, month, day] = bookingData.bookingDate.split('-').map(Number);
      const [hours, minutes] = bookingData.startTime.split(':').map(Number);
      const bookingDateTime = new Date(year, month - 1, day, hours, minutes);
      const now = new Date();
      const isPast = bookingDateTime < now;
      
      if (isPast) {
        setCancellationWarning('This booking is in the past and cannot be cancelled.');
        setWithinCancellationWindow(false);
      } else {
        // Calculate hours until booking - already using local time from above
        const hoursUntilBooking = (bookingDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        const cancellationWindowHours = bookingData.amenity?.cancellationWindowHours || 24;
        
        if (hoursUntilBooking < cancellationWindowHours) {
          setCancellationWarning(
            `Cancellation is within the ${cancellationWindowHours} hour window. You and all guests will be charged for this booking.`
          );
          setWithinCancellationWindow(false);
        } else {
          setWithinCancellationWindow(true);
        }
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching booking details:', err);
      setError(err?.response?.data?.message || 'Failed to load booking details');
      setLoading(false);
    }
  };



  const calculateFees = async (guests: BookingGuest[]) => {
    if (!booking || !bookingId) return;
    
    setLoadingFees(true);
    try {
      // console.log('[FEE CALCULATION] Calculating fees with guests:', guests);
      
      // Transform frontend guest format to backend format
      const transformedGuests = guests.map(g => {
        if (g.type === 'external') {
          // External guest: transform to backend format
          return {
            type: 'guest',
            memberId: null,
            guest: {
              name: g.name || null,
              email: g.email || null
            }
          };
        } else {
          // Member guest: transform to backend format
          return {
            type: 'member',
            memberId: g.memberId || null,
            guest: {
              name: g.name || null,
              email: g.email || null
            }
          };
        }
      });
      
      // console.log('[FEE CALCULATION] Transformed guests for backend:', transformedGuests);
      
      const response = await amenityAPI.calculateAmenityBookingFees(booking.amenityId, {
        bookingDate: booking.bookingDate,
        startTime: booking.startTime,
        endTime: booking.endTime,
        memberProfileId: currentMemberProfile?.id,
        guests: transformedGuests
      });
      
      // console.log('[FEE CALCULATION] Received fee breakdown:', response.data.feeBreakdown);
      setFeeBreakdown(response.data.feeBreakdown);
    } catch (err: any) {
      console.error('Error calculating fees:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingFees(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (proceedWithCharges: boolean = false) => {
    if (!booking) return;
    
    setCancelling(true);
    try {
      const requestData = withinCancellationWindow ? {} : { acknowledgeCharges: proceedWithCharges };
      
      await amenityAPI.cancelAmenityBooking(
        booking.amenityId,
        booking.id,
        requestData
      );
      
      setSuccessMessage('Booking cancelled successfully');
      setCancelDialogOpen(false);
      
      // Navigate back to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      setError(err?.response?.data?.message || 'Failed to cancel booking');
      setCancelling(false);
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit - reset guests
      setEditedGuests([...(booking?.guests || [])]);
    }
    setEditMode(!editMode);
  };

  const handleAddMemberGuest = () => {
    setEditedGuests([
      ...editedGuests,
      { type: 'member', name: '', email: '', memberId: '' }
    ]);
  };

  const handleAddExternalGuest = () => {
    setEditedGuests([
      ...editedGuests,
      { type: 'external', name: '', email: '' }
    ]);
  };

  const handleRemoveGuest = (index: number) => {
    const newGuests = editedGuests.filter((_, i) => i !== index);
    setEditedGuests(newGuests);
  };

  const handleGuestChange = (index: number, field: string, value: any) => {
    const newGuests = [...editedGuests];
    if (field === 'member') {
      // When selecting a member from autocomplete
      newGuests[index] = {
        type: 'member',
        memberId: value.id,
        name: `${value.firstName} ${value.lastName}`,
        email: value.email
      };
    } else {
      (newGuests[index] as any)[field] = value;
    }
    setEditedGuests(newGuests);
  };

  const handleSaveGuests = async () => {
    if (!booking) return;
    
    // Validate guests
    for (const guest of editedGuests) {
      // Members picked from the search box are identified by id (search results carry no email)
      if (!guest.name || (guest.type !== 'member' && !guest.email)) {
        setError('All guests must have a name and email');
        return;
      }
      if (guest.type === 'member' && !guest.memberId) {
        setError('Please select a valid member from the dropdown');
        return;
      }
    }
    
    // Calculate fees one more time to ensure accuracy
    await calculateFees(editedGuests);
    
    // Show confirmation dialog with cost breakdown
    setConfirmDialogOpen(true);
  };
  
  const handleConfirmSave = async () => {
    if (!booking) return;
    
    setConfirmDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      // Transform frontend guest format to backend format
      const transformedGuests = editedGuests.map(g => {
        /*
        When a guest is loaded from the backend, it has type: 'guest', but when enriching it, 
        we're not changing the type. Then when saving, the transformation logic only checks for type === 'external' 
        (for newly added guests) but doesn't handle existing guests with type === 'guest'. 
        */
        if (g.type === 'external' || g.type === 'guest') {
          // External guest: transform to backend format
          return {
            type: 'guest',
            memberId: null,
            guest: {
              name: g.name || null,
              email: g.email || null
            }
          };
        } else {
          // Member guest: transform to backend format
          return {
            type: 'member',
            memberId: g.memberId || null,
            guest: {
              name: g.name || null,
              email: g.email || null
            }
          };
        }
      });
      
      // console.log('[SAVE] Transformed guests for backend:', transformedGuests);
      
      // Call the update endpoint with transformed guests
      // The backend will handle transaction cancellation and creation
      await amenityAPI.updateAmenityBooking(
        booking.amenityId,
        booking.id,
        { guests: transformedGuests }
      );
      
      setSuccessMessage('Booking updated successfully');
      setEditMode(false);
      
      // Refresh booking details
      await fetchBookingDetails();
    } catch (err: any) {
      console.error('Error updating booking:', err);
      setError(err?.response?.data?.message || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !booking) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!booking) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Booking not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  // Safe date/time handling - avoid UTC conversion
  const [year, month, day] = booking.bookingDate.split('-').map(Number);
  const [hours, minutes] = booking.startTime.split(':').map(Number);
  const bookingDateTime = new Date(year, month - 1, day, hours, minutes);
  const isPastBooking = bookingDateTime < new Date();

  return (
    <Container ref={containerRef} maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Booking Details
        </Typography>
      </Box>

      {/* Booking Status Chip */}
      <Box sx={{ mb: 2 }}>
        <Chip
          label={booking.status.toUpperCase()}
          color={
            booking.status === 'confirmed'
              ? 'success'
              : booking.status === 'cancelled'
              ? 'error'
              : 'default'
          }
          size="medium"
        />
        {isPastBooking && (
          <Chip label="PAST" color="info" size="medium" sx={{ ml: 1 }} />
        )}
      </Box>

      {/* Main Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {booking.amenityName || 'Amenity Booking'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid size={{ xs:12, sm:6}}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(booking.bookingDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs:12, sm:6}}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Time
                </Typography>
                <Typography variant="body1">
                  {formatTimeString(booking.startTime)} - {formatTimeString(booking.endTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs:12, sm:6}}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Booking Fee
                </Typography>
                <Typography variant="body1">
                  ${Number(booking.bookingFee || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {booking.notes && (
            <Grid size={{ xs:12, sm:6}}>
              <Typography variant="body2" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body1">
                {booking.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Players/Guests Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Players ({1 + (editMode ? editedGuests.length : booking.guests.length)})
          </Typography>
          {!isPastBooking && booking.status !== 'cancelled' && (
            <Button
              startIcon={editMode ? <CancelIcon /> : <EditIcon />}
              onClick={handleEditToggle}
              variant={editMode ? 'outlined' : 'text'}
              disabled={saving}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </Button>
          )}
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Host - use booking.memberName instead of currentMemberProfile */}
        <List>
          <ListItem>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  <Typography variant="body1">
                    {booking.memberName || `${currentMemberProfile?.firstName} ${currentMemberProfile?.lastName}`}
                  </Typography>
                  <Chip label="Host" size="small" color="primary" sx={{ ml: 1 }} />
                  {booking.memberId === currentMemberProfile?.id && (
                    <Chip label="You" size="small" color="secondary" sx={{ ml: 1 }} />
                  )}
                </Box>
              }
              secondary={booking.memberEmail || currentMemberProfile?.email}
            />
          </ListItem>
          
          {/* Guests */}
          {editMode ? (
            <>
              {editedGuests.map((guest, index) => (
                <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <Box sx={{ display: 'flex', width: '100%', gap: 2, mb: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    {guest.type === 'member' ? (
                      <MemberSearchAutocomplete
                        size="small"
                        value={guest.memberId ? { id: guest.memberId, label: guest.name || 'Selected member' } : null}
                        excludeIds={[
                          ...(booking?.memberId ? [booking.memberId] : []),
                          ...editedGuests.filter((g, i) => i !== index && g.type === 'member' && g.memberId).map(g => g.memberId as string),
                        ]}
                        onSelect={option => {
                          if (option) {
                            handleGuestChange(index, 'member', {
                              id: option.id,
                              firstName: option.firstName,
                              lastName: option.lastName,
                              email: '',
                            });
                          }
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={guest.name}
                          onChange={(e) => handleGuestChange(index, 'name', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                        <TextField
                          fullWidth
                          label="Email"
                          value={guest.email}
                          onChange={(e) => handleGuestChange(index, 'email', e.target.value)}
                          variant="outlined"
                          size="small"
                          type="email"
                        />
                      </Box>
                    )}
                    <IconButton
                      onClick={() => handleRemoveGuest(index)}
                      color="error"
                      size="small"
                      sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box>
                    <Chip
                      label={guest.type === 'member' ? 'Member' : 'External Guest'}
                      size="small"
                      color={guest.type === 'member' ? 'primary' : 'default'}
                    />
                  </Box>
                </ListItem>
              ))}
              
              {/* Add Guest Buttons */}
              <ListItem>
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddMemberGuest}
                    variant="outlined"
                    size="small"
                  >
                    Member
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddExternalGuest}
                    variant="outlined"
                    size="small"
                  >
                    Guest
                  </Button>
                </Stack>
              </ListItem>
            </>
          ) : (
            booking.guests.map((guest, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                      <Typography variant="body1">{guest.name}</Typography>
                      <Chip
                        label={guest.type === 'member' ? 'Member' : 'Guest'}
                        size="small"
                        color={guest.type === 'member' ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={guest.email}
                />
              </ListItem>
            ))
          )}
        </List>
        
        {/* Save Button for Edit Mode */}
        {editMode && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveGuests}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Charges Section - Show current charges */}
      {!editMode && booking.transactions && booking.transactions.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Charges per Member
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {booking.transactions.map((charge, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body1">
                          {charge.memberName}
                          {charge.isHost && <Chip label="Host" size="small" color="primary" sx={{ ml: 1 }} />}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {charge.tierName}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body1" fontWeight="bold">
                          ${charge.totalAmount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (incl. ${charge.tax} tax)
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondary={
                    charge.metadata?.splitDetails && (
                      <Typography variant="caption" color="text.secondary">
                        {charge.metadata.splitDetails.feePerPayingPlayer > 0 && 
                          `Split: $${charge.metadata.splitDetails.feePerPayingPlayer.toFixed(2)}`}
                        {charge.metadata.splitDetails.hostGuestFees > 0 && 
                          ` | Guest fees: $${charge.metadata.splitDetails.hostGuestFees.toFixed(2)}`}
                      </Typography>
                    )
                  }
                />
                <Chip 
                  label={charge.status}
                  size="small"
                  color={charge.status === 'pending' ? 'warning' : charge.status === 'paid' ? 'success' : 'default'}
                  sx={{ ml: 2 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Live Fee Calculation in Edit Mode */}
      {editMode && feeBreakdown && (
        <Paper sx={{ p: 3, mb: 3, border: 1, borderColor: 'info.main' }}>
          <Typography variant="h6" gutterBottom color="info.dark">
            Cost Preview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loadingFees ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense>
              {/* Host - Always show, indicate if they have free access */}
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          You (Host)
                        </Typography>
                        {!feeBreakdown.host.shouldPay && feeBreakdown.host.bookingFee === 0 && (
                          <Chip label="Free Access" size="small" color="success" />
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {feeBreakdown.host.shouldPay || feeBreakdown.host.guestFees > 0 
                          ? `$${feeBreakdown.host.totalAmount.toFixed(2)}`
                          : 'FREE'}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    feeBreakdown.host.shouldPay || feeBreakdown.host.guestFees > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {feeBreakdown.host.bookingFee > 0 && `Split: $${feeBreakdown.host.bookingFee.toFixed(2)}`}
                        {feeBreakdown.host.guestFees > 0 && ` | Guest fees: $${feeBreakdown.host.guestFees.toFixed(2)}`}
                        {` | Tax: $${feeBreakdown.host.tax.toFixed(2)}`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="success.main">
                        Free tier access - no booking fee
                      </Typography>
                    )
                  }
                />
              </ListItem>
              
              {/* Member Guests - Show ALL member guests, not just those who pay */}
              {feeBreakdown.memberGuests.map((guest, idx) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">
                          {guest.name} {!guest.shouldPay && <Chip label={`${guest.tierName} - Free Access`} size="small" color="success" sx={{ ml: 1 }} />}
                        </Typography>
                        <Typography variant="body2">
                          {guest.shouldPay ? `$${guest.totalAmount.toFixed(2)}` : 'FREE'}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      guest.shouldPay ? (
                        <Typography variant="caption" color="text.secondary">
                          Split: ${guest.baseAmount.toFixed(2)} | Tax: ${guest.tax.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="success.main">
                          {guest.reason || 'Free tier access - no charge'}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
              
              {/* External Guests */}
              {feeBreakdown.externalGuests.length > 0 && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {feeBreakdown.externalGuests.length} external guest(s) - fees included in host's charge
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              
              <Divider sx={{ my: 1 }} />
              
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">
                        Total (All Members)
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${feeBreakdown.grandTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {feeBreakdown.totalPlayers} player(s) total | {feeBreakdown.payingPlayers} paying
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          )}
        </Paper>
      )}

      {/* Action Buttons */}
      {!isPastBooking && booking.status !== 'cancelled' && !editMode && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelClick}
            >
              Cancel Booking
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Cancellation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          {withinCancellationWindow ? (
            <DialogContentText>
              Are you sure you want to cancel this booking? All players will be notified and
              no charges will be applied since you are cancelling outside the cancellation window.
            </DialogContentText>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cancellationWarning}
              </Alert>
              <DialogContentText>
                {!isPastBooking ? (
                  <>
                    You are cancelling within the cancellation window. If you proceed:
                    <br />
                    • You and all guests will be charged for this booking
                    <br />
                    • All players will be notified of the cancellation
                    <br />
                    <br />
                    Do you want to proceed with the cancellation and accept the charges?
                  </>
                ) : (
                  'This booking cannot be cancelled as it is in the past.'
                )}
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            Keep Booking
          </Button>
          {!isPastBooking && (
            withinCancellationWindow ? (
              <Button
                onClick={() => handleCancelConfirm(false)}
                color="error"
                variant="contained"
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            ) : (
              <Button
                onClick={() => handleCancelConfirm(true)}
                color="error"
                variant="contained"
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Proceed with Charges'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Saving Changes */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !saving && setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Booking Changes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Review the updated charges for this booking:
          </DialogContentText>
          
          {feeBreakdown && (
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <List dense>
                {/* Host */}
                {feeBreakdown.host.shouldPay && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight="bold">
                            You (Host)
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            ${feeBreakdown.host.totalAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {feeBreakdown.host.bookingFee > 0 && `Split: $${feeBreakdown.host.bookingFee.toFixed(2)}`}
                          {feeBreakdown.host.guestFees > 0 && ` | Guest fees: $${feeBreakdown.host.guestFees.toFixed(2)}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {/* Member Guests */}
                {feeBreakdown.memberGuests.filter(g => g.shouldPay).map((guest, idx) => (
                  <ListItem key={idx} sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {guest.name}
                          </Typography>
                          <Typography variant="body2">
                            ${guest.totalAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                
                {feeBreakdown.memberGuests.filter(g => !g.shouldPay).length > 0 && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {feeBreakdown.memberGuests.filter(g => !g.shouldPay).length} member guest(s) with free access
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {feeBreakdown.externalGuests.length > 0 && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {feeBreakdown.externalGuests.length} external guest(s) - ${feeBreakdown.guestFeePerPerson.toFixed(2)} each (charged to host)
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" fontWeight="bold">
                          Total
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ${feeBreakdown.grandTotal.toFixed(2)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            These charges will be added to members' accounts and processed at the end of the billing period.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="primary"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Confirm & Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberAmenityBooking;
